import {
  Box,
  Center,
  CenterProps,
  chakra,
  Flex,
  Icon,
  useTab,
} from '@chakra-ui/react';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import React, { forwardRef } from 'react';
import { KText } from '@/ui-library/components/text';

interface CustomTabWithStepProps {
  step?: string;
  label: string;
  arrow?: boolean;
  onClick?: () => void;
  stepAfter?: boolean;
  stepStyles?: CenterProps;
  ref?: React.Ref<HTMLElement>;
  showError?: boolean;
  icon?: any;
  defaultTabSelectedStyles?: any;
  ChevronRightStyles?: any;
  labelStyles?: any;
  px?: any;
  testID?: string;
  isDisabled?: boolean;
}

const StyledTab = chakra('button', { themeKey: 'Tabs.Tab' });

export const CustomTabWithStep = forwardRef<
  HTMLButtonElement,
  CustomTabWithStepProps
>((props, ref) => {
  const {
    step,
    label,
    arrow = false,
    stepAfter = false,
    stepStyles = {},
    onClick,
    icon,
    showError = false,
    defaultTabSelectedStyles,
    ChevronRightStyles,
    labelStyles,
    testID = 'custom-tab',
    isDisabled = false,
    ...rest
  } = props;
  const tabProps = useTab({ ...rest, onClick, ref });
  const isSelected = !!tabProps['aria-selected'];

  const selectedStyles = defaultTabSelectedStyles || {
    color: 'primary-500',
    borderColor: 'primary-500',
    borderBottomWidth: '2px',
  };

  const getCursor = () => {
    if (isDisabled) return 'not-allowed';
    if (onClick) return 'pointer';
  };

  return (
    <>
      <StyledTab
        p="0 6px"
        py="8px"
        m="0 15px"
        _selected={isSelected ? selectedStyles : {}}
        cursor={getCursor()}
        onClick={onClick}
        _focusVisible={{ outline: 'none' }}
        opacity={isDisabled ? 0.6 : 1}
        _disabled={{
          cursor: 'not-allowed',
          opacity: 0.6,
        }}
        {...tabProps}
      >
        <Flex alignItems="center" {...labelStyles}>
          {step && !stepAfter && (
            <Center
              width="20px"
              height="20px"
              borderRadius="50%"
              bg={
                isDisabled
                  ? 'ats-bg-secondary'
                  : isSelected
                    ? 'rgba(90, 84, 255, 0.12)'
                    : 'ats-bg-primary'
              }
              color={
                isDisabled
                  ? 'secondary-400'
                  : isSelected
                    ? 'primary-500'
                    : 'secondary-700'
              }
              fontSize="12px"
              fontWeight="600"
              mr="12px"
              {...stepStyles}
            >
              {step}
            </Center>
          )}

          {icon && !stepAfter && <Icon mr="12px" as={icon} />}
          <KText
            color={
              isDisabled
                ? 'secondary-400'
                : isSelected
                  ? 'primary-500'
                  : 'secondary-700'
            }
            size="sm-semi-bold"
            data-test-id={testID}
          >
            {label}
          </KText>
          {showError && (
            <Box
              width="8px"
              height="8px"
              bg="red"
              ml="8px"
              borderRadius="full"
            />
          )}
          {step && stepAfter && (
            <Center
              width="20px"
              height="20px"
              bg={
                isDisabled
                  ? 'ats-bg-secondary'
                  : isSelected
                    ? 'rgba(90, 84, 255, 0.12)'
                    : 'ats-bg-primary'
              }
              color={
                isDisabled
                  ? 'secondary-400'
                  : isSelected
                    ? 'primary-500'
                    : 'secondary-700'
              }
              fontSize="sm"
              fontWeight="semiBold"
              p="2px 4.5px"
              borderRadius="2.67px"
              ml="12px"
              mr="4px"
              data-test-id={`${testID}-count`}
              {...stepStyles}
            >
              {step}
            </Center>
          )}
          {icon && stepAfter && <Icon ml="12px" mr="4px" as={icon} />}
        </Flex>
      </StyledTab>
      {arrow && (
        <Icon
          as={ChevronRight}
          width="15px"
          height="15px"
          color="secondary-700"
          ml="6px"
          mt="12px"
          {...ChevronRightStyles}
        />
      )}
    </>
  );
});

CustomTabWithStep.displayName = 'CustomTabWithStep';
